import useCustomTranslation from "@/localization/useCustomTranslation";
import { RouteHandle } from "@/types/header.types";
import { FC, useMemo } from "react";
import { useMatches, useSearchParams } from "react-router";
import { LanguageSelector, Profile } from "../index";
import { getLeftHeaderSlot, getRightHeaderSlot } from "./slot.util";
import { cn } from "@fido/ui-lib";
import { useLayoutRouteFlags } from "@/hooks/layout/useLayoutRouteFlags";

const HeaderDivider: FC = () => (
  <div className="min-w-[2px] max-h-[50%] h-full w-px bg-[#E4E6EE] rounded-lg" />
);

export const Header: FC = () => {
  const matches = useMatches();
  const [searchParams] = useSearchParams();
  const { prefixedT } = useCustomTranslation("HEADER");
  const { isFullWidthPage } = useLayoutRouteFlags();

  // Find the deepest route with header configuration
  const headerConfig = useMemo(() => {
    // Start from the deepest match and work backwards to find header config
    for (let i = matches.length - 1; i >= 0; i--) {
      const match = matches[i];
      const handle = match.handle as RouteHandle | undefined;
      if (handle?.header) {
        return handle.header;
      }
    }
    return null;
  }, [matches]);

  const headerSlotConfig = useMemo(() => {
    // Start from the deepest match and work backwards to find header config
    for (let i = matches.length - 1; i >= 0; i--) {
      const match = matches[i];
      const handle = match.handle as RouteHandle | undefined;
      if (handle?.header?.extraSlots?.length) {
        return handle.header.extraSlots;
      }
      if (handle?.header?.extraSlot) {
        return [handle.header.extraSlot];
      }
    }
    return [];
  }, [matches]);

  // Compute the final title
  const title = useMemo((): string => {
    if (!headerConfig?.titleKey) {
      return "";
    }

    if (typeof headerConfig.titleKey === "string") {
      // Static i18n key
      return prefixedT(headerConfig.titleKey);
    } else {
      // Dynamic title function
      const params = matches[matches.length - 1]?.params || {};
      return headerConfig.titleKey({
        params,
        search: searchParams,
        t: prefixedT as (
          key: string,
          options?: Record<string, unknown>
        ) => string
      });
    }
  }, [headerConfig, prefixedT, matches, searchParams]);

  const leftSlotComponents = useMemo(() => {
    return headerSlotConfig
      .map((slotKey) => ({
        slotKey,
        Component: getLeftHeaderSlot(slotKey)
      }))
      .filter(
        (
          slot
        ): slot is {
          slotKey: string;
          Component: NonNullable<typeof slot.Component>;
        } => !!slot.Component
      );
  }, [headerSlotConfig]);

  const rightSlotComponents = useMemo(() => {
    return headerSlotConfig
      .map((slotKey) => ({
        slotKey,
        Component: getRightHeaderSlot(slotKey)
      }))
      .filter(
        (
          slot
        ): slot is {
          slotKey: string;
          Component: NonNullable<typeof slot.Component>;
        } => !!slot.Component
      );
  }, [headerSlotConfig]);

  // If header is hidden, render nothing
  if (headerConfig?.hidden) {
    return null;
  }

  return (
    <div
      data-testid="header-container"
      className="h-(--header-height) bg-transparent relative z-50 "
    >
      <div
        className={cn(
          "flex flex-row items-center justify-between px-8 h-full",
          isFullWidthPage ? "w-full" : "container"
        )}
      >
        <div className="flex flex-row items-center h-full gap-4">
          {title && (
            <div className="flex items-center">
              <h5 className="text-h5 font-extrabold leading-[120%] tracking-[0.02em] capitalize">
                {title}
              </h5>
            </div>
          )}
          {leftSlotComponents.map(({ slotKey, Component }) => (
            <Component
              key={`left-${slotKey}`}
              params={matches[matches.length - 1]?.params || {}}
              search={searchParams}
            />
          ))}
        </div>
        <div className="flex flex-row items-center h-full gap-4">
          {rightSlotComponents.length > 0 && (
            <>
              {rightSlotComponents.map(({ slotKey, Component }) => (
                <Component
                  key={`right-${slotKey}`}
                  params={matches[matches.length - 1]?.params || {}}
                  search={searchParams}
                />
              ))}
              <HeaderDivider />
            </>
          )}
          <LanguageSelector />
          <HeaderDivider />
          <Profile />
        </div>
      </div>
    </div>
  );
};
